import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';

// Auto-logoff after 15 minutes without activity (HIPAA §164.312(a)(2)(iii)).
//
// Activity is shared across every tab of this browser through localStorage,
// and the logout is LOCAL to this browser. Before 2026-10-30 each tab kept
// its own idle timer and signed out GLOBALLY: a forgotten second tab, or the
// text link opened on a phone and left there, revoked the session on every
// device 15 minutes later — the front desk was kicked out mid-keystroke
// while setting an office's hours on a referral, over and over, each time
// they signed back in. Idle now means idle everywhere in this browser, and
// an idle phone no longer logs out the desktop.
const TIMEOUT_MS = 15 * 60 * 1000;
const KEY = 'pi-portal-last-active';
export const IDLE_FLAG = 'pi-portal-idle-logout';
const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'pointerdown', 'wheel'];

const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
const lastActive = () => Number(read(KEY)) || 0;

export function useSessionTimeout() {
  const { session } = useAuth();

  useEffect(() => {
    if (!session) return undefined;
    let timer = null;
    let lastTouch = 0;

    const schedule = (ms) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(check, Math.max(1000, ms));
    };
    // Fires when THIS tab has been quiet for 15 minutes — but another tab may
    // not have been. Re-check the shared stamp before doing anything.
    const check = () => {
      const idle = Date.now() - lastActive();
      if (idle < TIMEOUT_MS) { schedule(TIMEOUT_MS - idle); return; }
      try { sessionStorage.setItem(IDLE_FLAG, '1'); } catch {}
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    };
    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch < 2000) return; // mousemove fires constantly; one write every 2s is plenty
      lastTouch = now;
      write(KEY, String(now));
      schedule(TIMEOUT_MS);
    };
    // Another tab of this browser was active — push our own timer out too.
    const onStorage = (e) => { if (e.key === KEY && e.newValue) schedule(TIMEOUT_MS); };

    EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);
    write(KEY, String(Date.now()));
    schedule(TIMEOUT_MS);

    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      if (timer) clearTimeout(timer);
    };
  }, [session]);
}
